import React, { useState, useEffect, useMemo } from 'react';
import {
    Button,
    Chip,
    Box,
    Typography,
    Alert,
    Stack
} from '@mui/material';
import { Industry } from '../../../models/Industry';
import { getIndustries } from '../../../services/industryService';
import { createTestIndustry } from '../../../services/createTestIndustry';
import EditCatalogComponent from '../../../pages/catalog/edit-catalog-component/edit-catalog-component';
import { IndustryDetailsModal } from '../../../pages/catalog/industry-details-modal';
import { StatusChip } from '../../../components/shared/StyledTable';
import { SharedTable, Column } from '../../../components/shared/table/Table';
import './business-types-table.scss';
import { useDebounce } from '../../../hooks/useDebounce';

type Order = 'asc' | 'desc';
type OrderBy = 'createdAt' | 'name';
type StatusFilter = 'published' | 'pending' | 'archived';

const BusinessTypesTable: React.FC = () => {
    const [industries, setIndustries] = useState<Industry[]>([]);
    const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
    const [selectedIndustry, setSelectedIndustry] = useState<Industry | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [searchQuery, setSearchQuery] = useState('');
    const [order, setOrder] = useState<Order>('desc');
    const [orderBy, setOrderBy] = useState<OrderBy>('createdAt');
    const [selectedStatuses, setSelectedStatuses] = useState<StatusFilter[]>([]);

    const debouncedSearchQuery = useDebounce(searchQuery, 300);

    const handleRequestSort = (property: OrderBy) => {
        const isAsc = orderBy === property && order === 'asc';
        setOrder(isAsc ? 'desc' : 'asc');
        setOrderBy(property);
    };

    const handleStatusFilter = (status: StatusFilter) => {
        setSelectedStatuses(prev => {
            if (prev.includes(status)) {
                return prev.filter(s => s !== status);
            } else {
                return [...prev, status];
            }
        });
    };

    const sortedAndFilteredIndustries = useMemo(() => {
        let filtered = industries;

        // Apply status filter
        if (selectedStatuses.length > 0) {
            filtered = filtered.filter(industry => selectedStatuses.includes(industry.status as StatusFilter));
        }

        // Apply search filter
        if (debouncedSearchQuery) {
            filtered = filtered.filter(industry =>
                industry.name.toLowerCase().includes(debouncedSearchQuery.toLowerCase())
            );
        }

        // Apply sorting
        return filtered.sort((a, b) => {
            if (orderBy === 'createdAt') {
                const dateA = a.createdAt instanceof Date ? a.createdAt : new Date(a.createdAt);
                const dateB = b.createdAt instanceof Date ? b.createdAt : new Date(b.createdAt);
                return (order === 'asc' ? dateA.getTime() - dateB.getTime() : dateB.getTime() - dateA.getTime());
            } else {
                const nameA = a.name.toLowerCase();
                const nameB = b.name.toLowerCase();
                return (order === 'asc' ? nameA.localeCompare(nameB) : nameB.localeCompare(nameA));
            }
        });
    }, [industries, debouncedSearchQuery, order, orderBy, selectedStatuses]);

    const loadIndustries = async () => {
        try {
            setLoading(true);
            setError(null);
            const data = await getIndustries();
            setIndustries(data);

            if (data.length === 0) {
                await createTestIndustry();
                const updatedData = await getIndustries();
                setIndustries(updatedData);
            }
        } catch (error) {
            setError(error instanceof Error ? error.message : 'An error occurred while loading industries');
            console.error('Error loading industries:', error);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        loadIndustries();
    }, []);

    const handleAddNew = () => {
        setSelectedIndustry(null);
        setIsEditDialogOpen(true);
    };

    const handleCloseDialog = async () => {
        setIsEditDialogOpen(false);
        setSelectedIndustry(null);
        await loadIndustries();
    };

    const handleRowClick = (industry: Industry) => {
        setSelectedIndustry(industry);
        setIsModalOpen(true);
    };

    const handleCloseModal = () => {
        setIsModalOpen(false);
        setSelectedIndustry(null);
    };

    const handleSave = () => {
        loadIndustries();
    };

    const columns: Column<Industry>[] = [
        {
            id: 'createdAt',
            label: 'Created At',
            sortable: true,
            render: (row) => new Date(row.createdAt).toLocaleDateString()
        },
        {
            id: 'name',
            label: 'Name',
            sortable: true,
            render: (row) => row.name
        },
        {
            id: 'status',
            label: 'Status',
            render: (row) => (
                <StatusChip
                    label={row.status}
                    status={row.status}
                    size="small"
                />
            )
        }
    ];

    return (
        <div className="business-types-table">
            <Stack spacing={2} sx={{ mb: 2 }}>
                <Box sx={{ display: 'flex', gap: 1 }}>
                    <Chip
                        label="Published"
                        color="success"
                        variant={selectedStatuses.includes('published') ? 'filled' : 'outlined'}
                        onClick={() => handleStatusFilter('published')}
                        sx={{ cursor: 'pointer' }}
                    />
                    <Chip
                        label="Pending"
                        color="warning"
                        variant={selectedStatuses.includes('pending') ? 'filled' : 'outlined'}
                        onClick={() => handleStatusFilter('pending')}
                        sx={{ cursor: 'pointer' }}
                    />
                    <Chip
                        label="Archived"
                        color="error"
                        variant={selectedStatuses.includes('archived') ? 'filled' : 'outlined'}
                        onClick={() => handleStatusFilter('archived')}
                        sx={{ cursor: 'pointer' }}
                    />
                </Box>
            </Stack>

            <SharedTable
                title="Industry Types"
                columns={columns}
                data={sortedAndFilteredIndustries}
                loading={loading}
                error={error}
                orderBy={orderBy}
                order={order}
                searchQuery={searchQuery}
                searchPlaceholder="Search industries..."
                onSearchChange={setSearchQuery}
                onRequestSort={handleRequestSort}
                onRowClick={handleRowClick}
                getRowId={(row) => row.id}
                headerContent={
                    <Button
                        variant="contained"
                        color="primary"
                        onClick={handleAddNew}
                    >
                        Add New Industry
                    </Button>
                }
                emptyStateMessage="No industries found"
            />

            <EditCatalogComponent
                open={isEditDialogOpen}
                onClose={handleCloseDialog}
                industry={selectedIndustry}
            />

            {selectedIndustry && (
                <IndustryDetailsModal
                    open={isModalOpen}
                    onClose={handleCloseModal}
                    industry={selectedIndustry}
                    onSave={handleSave}
                />
            )}
        </div>
    );
};

export default BusinessTypesTable; 